import { TSelectOpt } from 'transfix-ui/components/Select/SelectTypes'
import { IShipperApplicationStates, IRateTypeTypes } from 'types/graphqlTypes'
import { SHIPMENT_TRACKING_LEVELS, IShipperTrackingSegment } from 'types/shipment'
import { capitalize } from 'utils/capitalize'
import { generateCohortYears } from '../utils/shipperHelpers'
import { TShipperCreditCheckStatus } from '../utils/types/shipper'

export enum SHIPPER_TABS {
  SHIPPER_OVERVIEW_TAB = 'shipper_overview',
  OPERATIONS_TAB = 'operations',
  FINANCE_TAB = 'finance',
  USERS_TAB = 'users',
  LOGS_TAB = 'logs',
  EDI_TAB = 'edi',
  FACILITIES = 'facilities',
  TRAILERS = 'trailers',
  CONTACTS = 'contacts',
}

export const SHIPPER_PROFILE_TABS = {
  [SHIPPER_TABS.SHIPPER_OVERVIEW_TAB]: SHIPPER_TABS.SHIPPER_OVERVIEW_TAB,
  [SHIPPER_TABS.OPERATIONS_TAB]: SHIPPER_TABS.OPERATIONS_TAB,
  [SHIPPER_TABS.FINANCE_TAB]: SHIPPER_TABS.FINANCE_TAB,
  [SHIPPER_TABS.USERS_TAB]: SHIPPER_TABS.USERS_TAB,
  [SHIPPER_TABS.LOGS_TAB]: SHIPPER_TABS.LOGS_TAB,
  [SHIPPER_TABS.EDI_TAB]: SHIPPER_TABS.EDI_TAB,
  [SHIPPER_TABS.TRAILERS]: SHIPPER_TABS.TRAILERS,
  [SHIPPER_TABS.FACILITIES]: SHIPPER_TABS.FACILITIES,
  [SHIPPER_TABS.CONTACTS]: SHIPPER_TABS.CONTACTS,
}

// TODO: determine which statuses to use for shipment count
export const DEFAULT_SHIPMENT_FILTERS = {
  filters: {
    status: [
      'e_submitted',
      'submitted',
      'quoted',
      'on_hold',
      'tendered',
      'waterfall_offered',
      'alerted',
      'reserved',
      'covered',
      'offered',
      'assigned',
      'dispatched',
      'arrived_for_pickup',
      'in_transit',
      'arrived_for_delivery',
    ],
    usersType: 'shipper',
  },
}

export const DEFAULT_USERS_FILTERS = {
  sortField: 'created_at',
}

export const INVOICING_METHODS = [
  {
    value: 'Mail',
    label: 'Physical Mail',
  },
  {
    value: 'Automated',
    label: 'EDI or Automated',
  },
  {
    value: 'Email',
    label: 'Email',
  },
]

export const PAYMENT_PREFERENCES = [
  {
    value: 'ACH',
    label: 'ACH',
  },
  {
    value: 'Credit Card (3% fee charged)',
    label: 'Credit Card (3% fee charged)',
  },
  {
    value: 'Checks',
    label: 'Checks',
  },
]

export const SUPER_ACCOUNTING = 'Super Accounting'
export const SALES_OP_MANAGER = 'Sales Op Manager'
export const ABLE_TO_EDIT_INVOICING_PREFS = [SUPER_ACCOUNTING, SALES_OP_MANAGER]

export const POD_OPTIONS_LEGACY = [
  {
    label: 'Pod 1',
    value: 1,
  },
  {
    label: 'Pod 2',
    value: 2,
  },
  {
    label: 'Pod 3',
    value: 3,
  },
  {
    label: 'Pod 4',
    value: 4,
  },
]

export const POD_OPTIONS_NEW = [
  {
    label: 'Orange Pod',
    value: 1,
  },
  {
    label: 'Green Pod',
    value: 2,
  },
  {
    label: 'Red Pod',
    value: 3,
  },
  {
    label: 'Yellow Pod',
    value: 4,
  },
  // ID 5 is a deleted POD, so Blue is 6
  {
    label: 'Blue Pod',
    value: 6,
  },
]

export const RATE_TYPES_OPTIONS: TSelectOpt[] = [
  {
    label: 'Total',
    value: IRateTypeTypes.total,
  },
  {
    label: 'Per Mile',
    value: IRateTypeTypes.per_miles,
  },
]

export const RATE_TYPES_EXPECTED_OPTIONS: TSelectOpt[] = [
  {
    label: 'Contract',
    value: 'Contract',
  },
  {
    label: 'BU/Published',
    value: 'BU/Published',
  },
  {
    label: 'Spot',
    value: 'Spot',
  },
  {
    label: 'Backhaul',
    value: 'Backhaul',
  },
]

export const SEASONALITY_OPTIONS: TSelectOpt[] = [
  {
    label: 'Spring',
    value: 'Spring',
  },
  {
    label: 'Summer',
    value: 'Summer',
  },
  {
    label: 'Fall',
    value: 'Fall',
  },
  {
    label: 'Winter',
    value: 'Winter',
  },
]

export const VERTICAL_OPTIONS = [
  {
    label: 'Food & Bev',
    value: 'Food & Bev',
  },
  {
    label: 'CPG',
    value: 'CPG',
  },
  {
    label: 'Commercial/Industrial/Automotive',
    value: 'Commercial/Industrial/Automotive',
  },
  {
    label: 'Retail',
    value: 'Retail',
  },
]

export const SEGMENT_OPTIONS = [
  {
    label: 'SMB',
    value: 'SMB',
  },
  {
    label: 'Enterprise',
    value: 'Enterprise',
  },
  {
    label: 'Mid Market',
    value: 'Mid Market',
  },
]

export const STATE_OPTIONS = Object.values(IShipperApplicationStates).map(value => ({
  label: capitalize(value),
  value,
}))

export const COHORT_YEARS_OPTIONS = generateCohortYears()

export const COHORT_QUARTERS_OPTIONS = [
  {
    label: 'Q1',
    value: 1,
  },
  {
    label: 'Q2',
    value: 2,
  },
  {
    label: 'Q3',
    value: 3,
  },
  {
    label: 'Q4',
    value: 4,
  },
]

export enum TRACKING_PERCENTAGE {
  NONE = 0,
  OPTIONAL = 25,
  ENCOURAGED = 50,
  REQUIRED = 100,
}

export const TRACKING_SEGMENTS_OPTIONS: IShipperTrackingSegment[] = [
  {
    id: '4',
    minReqTrackingPercentage: TRACKING_PERCENTAGE.REQUIRED,
    description: 'Must Track',
    level: SHIPMENT_TRACKING_LEVELS.REQUIRED,
  },
  {
    id: '3',
    minReqTrackingPercentage: TRACKING_PERCENTAGE.ENCOURAGED,
    description: 'Tracking Strongly Encouraged',
    level: SHIPMENT_TRACKING_LEVELS.ENCOURAGED,
  },
  {
    id: '2',
    minReqTrackingPercentage: TRACKING_PERCENTAGE.OPTIONAL,
    description: 'Tracking Nice To Have',
    level: SHIPMENT_TRACKING_LEVELS.OPTIONAL,
  },
  {
    id: '1',
    minReqTrackingPercentage: TRACKING_PERCENTAGE.NONE,
    description: 'Tracking Not Required',
    level: SHIPMENT_TRACKING_LEVELS.NONE,
  },
]

export const WORKERS_COMPENSATION_OPTIONS = [
  {
    label: 'Not required',
    value: 'Not required',
  },
  {
    label: 'Required by law',
    value: 'Required by law',
  },
  {
    label: 'Required with statutory limits',
    value: 'Required with statutory limits',
  },
]

export const DAYS_OF_WEEK_OPTIONS = [
  {
    label: 'Sunday',
    value: 'sunday',
  },
  {
    label: 'Monday',
    value: 'monday',
  },
  {
    label: 'Tuesday',
    value: 'tuesday',
  },
  {
    label: 'Wednesday',
    value: 'wednesday',
  },
  {
    label: 'Thursday',
    value: 'thursday',
  },
  {
    label: 'Friday',
    value: 'friday',
  },
  {
    label: 'Saturday',
    value: 'saturday',
  },
]

export const CREDIT_STATUS_OPTIONS: { value: TShipperCreditCheckStatus; label: string }[] = [
  {
    value: 'in_review',
    label: 'Pending',
  },
  {
    value: 'approved',
    label: 'Approved',
  },
  {
    value: 'declined',
    label: 'Declined',
  },
]
